import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaSearch, FaTruck, FaCheckCircle, FaClock, FaBan, FaTimes, FaEye, FaFileUpload
} from "react-icons/fa";
import { Row, Col } from "react-bootstrap";
import Pagination from "../../../components/general/pagination/Pagination";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import "../../../assets/css/admin/delivery/DeliveryMen.css";
import { DeliveryMenSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const DeliveryMen = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [deliveryMenList, setDeliveryMenList] = useState([]);
  const [searchTerm, setSearchTerm]           = useState("");
  const [currentPage, setCurrentPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage]       = useState(10);
  const [activeTab, setActiveTab]             = useState("all");
  const [openActionMenu, setOpenActionMenu]   = useState(null);
  const [showViewModal, setShowViewModal]     = useState(false);
  const [selectedDeliveryMan, setSelectedDeliveryMan] = useState(null);

  // ── Stats ──
  const [totalCount, setTotalCount]     = useState(0);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [pendingCount, setPendingCount]   = useState(0);
  const [blockedCount, setBlockedCount]   = useState(0);
  const [notSubmittedCount, setNotSubmittedCount] = useState(0);

  // ══════════════════════════════════════════════════════
  // API — viewAllDeliveryMen
  // ══════════════════════════════════════════════════════
  const viewAllDeliveryMen = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/delivery/viewAllDeliveryMen`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          const list = response.data.deliverymen;
          setDeliveryMenList(list);
          setTotalCount(list.length);
          setVerifiedCount(list.filter(d => d.is_verified === "approved").length);
          setPendingCount(list.filter(d => d.is_verified === "pending").length);
          setBlockedCount(list.filter(d => d.status === "blocked").length);
          setNotSubmittedCount(list.filter(d => !d.documents).length);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load delivery men." });
      });
  };

  // ══════════════════════════════════════════════════════
  // API — updateKycStatus (approve/reject)
  // ══════════════════════════════════════════════════════
  const updateKycStatus = (deliveryman, status) => {
    const formData = new FormData();
    formData.append("kmdeliveryman_id", deliveryman.id);
    formData.append("is_verified", status);

    axios
      .post(`${baseUrl}/admin/delivery/updateKycStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ icon: "success", title: "Success", text: `KYC ${status} successfully!` });
          viewAllDeliveryMen();
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ══════════════════════════════════════════════════════
  // API — updateAccountStatus (block/unblock)
  // ══════════════════════════════════════════════════════
  const updateAccountStatus = (deliveryman) => {
    const formData  = new FormData();
    formData.append("kmdeliveryman_id", deliveryman.id);

    axios
      .post(`${baseUrl}/admin/delivery/updateAccountStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ icon: "success", title: "Success", text: "Account status updated successfully!" });
          viewAllDeliveryMen();
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ══════════════════════════════════════════════════════
  // API — deleteDeliveryMan (sirf unke liye jinhone KYC submit nahi ki)
  // ══════════════════════════════════════════════════════
  const deleteDeliveryMan = (deliveryman) => {
    Swal.fire({
      title: "Delete Delivery Man?",
      text: `${deliveryman.name} ka account permanently delete ho jayega. Yeh action wapis nahi ho sakta.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const formData = new FormData();
      formData.append("kmdeliveryman_id", deliveryman.id);

      axios
        .post(`${baseUrl}/admin/delivery/deleteDeliveryMan`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          if (response.data.success) {
            Swal.fire({ icon: "success", title: "Deleted!", text: "Delivery man deleted successfully." });
            viewAllDeliveryMen();
          } else {
            Swal.fire({ icon: "error", title: "Error", text: response.data.message });
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({ icon: "error", title: "Error", text: "Failed to delete delivery man." });
        });
    });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllDeliveryMen();
  }, [authToken]);

  // ── Filter ──
  const filteredList = deliveryMenList.filter((d) => {
    const matchSearch = d.name?.toLowerCase().includes(searchTerm.toLowerCase())
      || d.phone?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchTab = true;
    if (activeTab === "verified") {
      matchTab = d.is_verified === "approved";
    } else if (activeTab === "pending") {
      matchTab = d.is_verified === "pending";
    } else if (activeTab === "notSubmitted") {
      matchTab = !d.documents;
    }

    return matchSearch && matchTab;
  });

  // ── Actions ──
  const getActions = (deliveryman) => {
    const actions = [
      {
        label: "View Details",
        onClick: () => {
          setSelectedDeliveryMan(deliveryman);
          setShowViewModal(true);
          setOpenActionMenu(null);
        },
      },
      {
        label: deliveryman.status === "active" ? "Block" : "Unblock",
        onClick: () => {
          updateAccountStatus(deliveryman);
          setOpenActionMenu(null);
        },
        variant: deliveryman.status === "active" ? "warning" : "success",
      },
    ];

    if (deliveryman.is_verified === "pending") {
      actions.push(
        {
          label: "Approve KYC",
          onClick: () => {
            updateKycStatus(deliveryman, "approved");
            setOpenActionMenu(null);
          },
          variant: "success",
        },
        {
          label: "Reject KYC",
          onClick: () => {
            updateKycStatus(deliveryman, "rejected");
            setOpenActionMenu(null);
          },
          variant: "danger",
        }
      );
    }

    // ── sirf jinhone documents submit hi nahi kiye, unke liye Delete ──
    if (!deliveryman.documents) {
      actions.push({
        label: "Delete",
        onClick: () => {
          deleteDeliveryMan(deliveryman);
          setOpenActionMenu(null);
        },
        variant: "danger",
      });
    }

    return actions;
  };

  // ── Stats ──
  const statsData = [
    {
      icon: FaTruck,
      iconBg: "linear-gradient(135deg,#e3f2fd,#bbdefb)",
      iconColor: "#2196f3",
      label: "Total Delivery Men",
      value: totalCount.toString(),
      badge: "All",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg,#e8f5f0,#d4ede3)",
      iconColor: "#0ac46c",
      label: "Verified",
      value: verifiedCount.toString(),
      badge: "KYC Done",
    },
    {
      icon: FaClock,
      iconBg: "linear-gradient(135deg,#fff3e0,#ffe0b2)",
      iconColor: "#ff9800",
      label: "Pending Verification",
      value: pendingCount.toString(),
      badge: "Pending",
    },
    {
      icon: FaFileUpload,
      iconBg: "linear-gradient(135deg,#f3e5f5,#e1bee7)",
      iconColor: "#9c27b0",
      label: "KYC Not Submitted",
      value: notSubmittedCount.toString(),
      badge: "No Docs",
    },
    {
      icon: FaBan,
      iconBg: "linear-gradient(135deg,#ffebee,#ffcdd2)",
      iconColor: "#f44336",
      label: "Blocked",
      value: blockedCount.toString(),
      badge: "Blocked",
    },
  ];

  const getKycBadge = (status, hasDocuments) => {
    if (!hasDocuments) {
      return <span className="ksa-dm-badge ksa-dm-badge-not-submitted">Not Submitted</span>;
    }
    const map = {
      approved: "ksa-dm-badge-approved",
      pending:  "ksa-dm-badge-pending",
      rejected: "ksa-dm-badge-rejected",
    };
    const labels = { approved: "Verified", pending: "Pending", rejected: "Rejected" };
    return <span className={`ksa-dm-badge ${map[status] || map["pending"]}`}>{labels[status] || "Pending"}</span>;
  };

  const getStatusBadge = (status) => (
    <span className={`ksa-dm-badge ${status === "active" ? "ksa-dm-badge-approved" : "ksa-dm-badge-rejected"}`}>
      {status === "active" ? "Active" : "Blocked"}
    </span>
  );

  // ── FIXED: pehle "vehicle_capacity" naam ka field use ho raha tha
  // jo DB mein exist hi nahi karta — table mein actually
  // "vehicle_capacity_min" aur "vehicle_capacity_max" do alag columns
  // hain. Ab dono ko combine karke dikhate hain ──
  const getCapacityText = (d) => {
    if (d.vehicle_capacity_min == null && d.vehicle_capacity_max == null) return "—";
    return `${d.vehicle_capacity_min ?? "?"} - ${d.vehicle_capacity_max ?? "?"} kg`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <DeliveryMenSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="ksa-dm-container">

        {/* ── Header ── */}
        <div className="ksa-dm-header">
          <div>
            <h1>Delivery Men Management</h1>
            <p>Delivery boys ko manage karein, KYC verify karein aur account status update karein</p>
          </div>
        </div>

        {/* ── Stats ── */}
        <Row className="g-4 mb-4">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} xl={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* ── Tabs ── */}
        <div className="ksa-dm-tabs">
          <button
            className={`ksa-dm-tab ${activeTab === "all" ? "ksa-dm-tab--active" : ""}`}
            onClick={() => { setActiveTab("all"); setCurrentPage(1); }}
          >
            All Delivery Boys
          </button>
          <button
            className={`ksa-dm-tab ${activeTab === "verified" ? "ksa-dm-tab--active" : ""}`}
            onClick={() => { setActiveTab("verified"); setCurrentPage(1); }}
          >
            Verified
            {verifiedCount > 0 && <span className="ksa-dm-tab-badge">{verifiedCount}</span>}
          </button>
          <button
            className={`ksa-dm-tab ${activeTab === "pending" ? "ksa-dm-tab--active" : ""}`}
            onClick={() => { setActiveTab("pending"); setCurrentPage(1); }}
          >
            Pending Verification
            {pendingCount > 0 && <span className="ksa-dm-tab-badge">{pendingCount}</span>}
          </button>
          <button
            className={`ksa-dm-tab ${activeTab === "notSubmitted" ? "ksa-dm-tab--active" : ""}`}
            onClick={() => { setActiveTab("notSubmitted"); setCurrentPage(1); }}
          >
            KYC Not Submitted
            {notSubmittedCount > 0 && <span className="ksa-dm-tab-badge">{notSubmittedCount}</span>}
          </button>
        </div>

        {/* ── Table ── */}
        <div className="ksa-dm-table-section">
          <div className="ksa-dm-table-header">
            <div className="ksa-dm-search">
              <FaSearch size={14} />
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="ksa-dm-table-wrapper">
            <table className="ksa-dm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Vehicle</th>
                  <th>Registered On</th>
                  <th>KYC Status</th>
                  <th>Account Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="ksa-dm-empty">No delivery men found</td>
                  </tr>
                ) : (
                  filteredList
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((d, i) => (
                      <tr key={d.id}>
                        <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                        <td className="ksa-dm-name">
                          {d.image ? (
                            <img src={`${baseUrlForFiles}/${d.image}`} alt={d.name} className="ksa-dm-avatar" />
                          ) : (
                            <div className="ksa-dm-avatar-placeholder">{d.name?.charAt(0)}</div>
                          )}
                          {d.name}
                        </td>
                        <td>{d.phone}</td>
                        <td>{d.city}</td>
                        {/* FIXED: "vechile_type" typo -> "vehicle_type" (asal DB/API field name) */}
                        <td>{d.vehicle_type || "—"}</td>
                        <td>
                          {new Date(d.created_at).toLocaleDateString()}{" "}
                          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                            {new Date(d.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </td>
                        <td>{getKycBadge(d.is_verified, d.documents)}</td>
                        <td>{getStatusBadge(d.status)}</td>
                        <td>
                          <ActionDropdown
                            isOpen={openActionMenu === d.id}
                            onToggle={() => setOpenActionMenu(openActionMenu === d.id ? null : d.id)}
                            actions={getActions(d)}
                          />
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={filteredList.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
        </div>

      </div>

      {/* ── View Modal ── */}
      {showViewModal && selectedDeliveryMan && (
        <div className="ksa-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="ksa-modal ksa-modal-lg ksa-category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ksa-category-modal-header">
              <h3 className="ksa-category-modal-title">Delivery Boy Details</h3>
              <button className="ksa-modal-close" onClick={() => setShowViewModal(false)}>
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-category-modal-body">
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Name</span>
                    <p className="ksa-dm-detail-value">{selectedDeliveryMan.name || "—"}</p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Phone</span>
                    <p className="ksa-dm-detail-value">{selectedDeliveryMan.phone || "—"}</p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">City</span>
                    <p className="ksa-dm-detail-value">{selectedDeliveryMan.city || "—"}</p>
                  </div>
                </Col>
                {/* ── NEW: Service Cities — jin cities mein driver
                     intercity delivery ke liye willing hai (Delivery
                     Cities tab se aata hai), admin ko bhi nazar aana
                     chahiye ── */}
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Service Cities</span>
                    <p className="ksa-dm-detail-value">
                      {Array.isArray(selectedDeliveryMan.service_cities) &&
                      selectedDeliveryMan.service_cities.length > 0
                        ? selectedDeliveryMan.service_cities.join(", ")
                        : "—"}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">CNIC</span>
                    <p className="ksa-dm-detail-value">{selectedDeliveryMan.cnic || "—"}</p>
                  </div>
                </Col>
                {/* FIXED: "vechile_type" typo -> "vehicle_type" */}
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Vehicle Type</span>
                    <p className="ksa-dm-detail-value">{selectedDeliveryMan.vehicle_type || "—"}</p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Vehicle Number</span>
                    <p className="ksa-dm-detail-value">{selectedDeliveryMan.vehicle_number || "—"}</p>
                  </div>
                </Col>
                {/* FIXED: "vehicle_capacity" (jo DB mein exist hi nahi
                    karta) ki jagah ab "vehicle_capacity_min" aur
                    "vehicle_capacity_max" dono combine karke dikhaye
                    ja rahe hain */}
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Vehicle Capacity</span>
                    <p className="ksa-dm-detail-value">{getCapacityText(selectedDeliveryMan)}</p>
                  </div>
                </Col>
                {/* REMOVED: "Vehicle Rate" field — kmdeliverymen table
                    mein koi "vehicle_rate" column exist hi nahi karta,
                    is liye yeh hamesha "—" dikhata tha. Agar future mein
                    chahiye to pehle DB migration se column add karna
                    hoga, phir yahan dobara add kar sakte hain. */}
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">KYC Status</span>
                    <p className="ksa-dm-detail-value">
                      {getKycBadge(selectedDeliveryMan.is_verified, selectedDeliveryMan.documents)}
                    </p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="ksa-dm-detail-group">
                    <span className="ksa-dm-detail-label">Account Status</span>
                    <p className="ksa-dm-detail-value">{getStatusBadge(selectedDeliveryMan.status)}</p>
                  </div>
                </Col>
                {selectedDeliveryMan.documents && (
                  <Col xs={12}>
                    <div className="ksa-dm-detail-group">
                      <span className="ksa-dm-detail-label">KYC Documents</span>
                      <a
                        href={`${baseUrlForFiles}/${selectedDeliveryMan.documents}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ksa-dm-doc-link"
                      >
                        View KYC PDF
                      </a>
                    </div>
                  </Col>
                )}
              </Row>
            </div>

            <div className="ksa-category-modal-footer">
              <button className="outline-gradient" onClick={() => setShowViewModal(false)}>Close</button>
              {selectedDeliveryMan.is_verified === "pending" && (
                <>
                  <button className="ksa-dm-reject-btn" onClick={() => { updateKycStatus(selectedDeliveryMan, "rejected"); setShowViewModal(false); }}>
                    Reject KYC
                  </button>
                  <button className="ksa-dm-approve-btn" onClick={() => { updateKycStatus(selectedDeliveryMan, "approved"); setShowViewModal(false); }}>
                    Approve KYC
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
};

export default DeliveryMen;
